import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getClient, getClientReviews, reportReview, deleteMyReview, updateMyReview } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Star, Plus, Calendar, User, Flag, MapPin, Trash2, Pencil, X } from 'lucide-react';

const RatingBar = ({ label, value }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-sm">
      <span className="text-stone-600">{label}</span>
      <span className="font-medium text-stone-900">{value}/5</span>
    </div>
    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
      <div 
        className="h-full bg-[#D97757] rounded-full rating-bar-fill" 
        style={{ width: `${(value / 5) * 100}%` }}
      />
    </div>
  </div>
);

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportingId, setReportingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const openEditModal = (review) => {
    setEditingReview(review);
    setEditFormData({
      service_quality_rating: review.service_quality_rating,
      punctuality_rating: review.punctuality_rating,
      payment_rating: review.payment_rating,
      communication_rating: review.communication_rating,
      service_notes: review.service_notes || '',
      general_notes: review.general_notes || '',
      is_anonymous: review.is_anonymous || false
    });
  };

  const closeEditModal = () => {
    setEditingReview(null);
    setEditFormData(null);
  };

  const handleSaveEdit = async () => {
    if (!editingReview || !editFormData) return;
    
    setSavingEdit(true);
    try {
      const updatedReview = await updateMyReview(editingReview.id, editFormData);
      setReviews(reviews.map(r => r.id === editingReview.id ? updatedReview : r));
      toast.success('Review updated successfully');
      closeEditModal();
      
      // Refresh client data to update average rating
      const clientData = await getClient(clientId);
      setClient(clientData);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update review';
      toast.error(message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (deletingId) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to delete this review? This action cannot be undone.'
    );
    
    if (!confirmed) return;
    
    setDeletingId(reviewId);
    try {
      await deleteMyReview(reviewId);
      setReviews(reviews.filter(r => r.id !== reviewId));
      toast.success('Review deleted successfully');
      
      // Refresh client data to update review count
      const clientData = await getClient(clientId);
      setClient(clientData);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete review';
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleReportReview = async (reviewId) => {
    if (reportingId) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to report this review as inappropriate? Our team will review it within 24-48 hours.'
    );
    
    if (!confirmed) return;
    
    setReportingId(reviewId);
    try {
      await reportReview(reviewId, 'inappropriate', 'Reported via review page');
      toast.success('Report submitted successfully. We will review it shortly.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit report';
      toast.error(message);
    } finally {
      setReportingId(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientData, reviewsData] = await Promise.all([
          getClient(clientId),
          getClientReviews(clientId)
        ]);
        setClient(clientData);
        setReviews(reviewsData);
      } catch (error) {
        console.error('Error fetching client:', error);
        toast.error('Failed to load client');
        navigate('/clients');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [clientId, navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center text-stone-500">Loading client...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center text-stone-500">Client not found</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="client-detail-page">
        {/* Back Button */}
        <Link 
          to="/clients"
          className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Clients
        </Link>

        {/* Client Header */}
        <Card className="mb-8 border-stone-100 shadow-sm overflow-hidden" data-testid="client-header-card">
          <div className="bg-gradient-to-r from-stone-900 to-stone-800 p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">
                    {client.first_name.charAt(0)}{(client.last_name || client.last_initial || '')?.charAt(0) || ''}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-white">
                    {client.first_name} {client.last_name || (client.last_initial ? `${client.last_initial}.` : '')}
                  </h1>
                  <div className="flex items-center gap-4 mt-2 text-stone-300 text-sm">
                    {client.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {client.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Link to={`/review/${clientId}`}>
                <Button 
                  className="h-11 px-6 rounded-full bg-[#D97757] text-white hover:bg-[#C06040]"
                  data-testid="add-review-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Review
                </Button>
              </Link>
            </div>
          </div>
          
          <CardContent className="p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center p-4 rounded-xl bg-stone-50">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Star className={`w-6 h-6 ${client.avg_rating > 0 ? 'fill-[#D97757] text-[#D97757]' : 'text-stone-300'}`} />
                  <span className="text-3xl font-bold text-stone-900">
                    {client.avg_rating > 0 ? client.avg_rating.toFixed(1) : '—'}
                  </span>
                </div>
                <p className="text-sm text-stone-600">Average Rating</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-stone-50">
                <p className="text-3xl font-bold text-stone-900">{client.review_count}</p>
                <p className="text-sm text-stone-600">Total Reviews</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-stone-50">
                <p className="text-sm font-medium text-stone-900">{formatDate(client.created_at)}</p>
                <p className="text-sm text-stone-600">Added On</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reviews Section */}
        <Card className="border-stone-100 shadow-sm" data-testid="client-reviews-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-stone-900">
              Reviews ({reviews.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reviews.length === 0 ? (
              <div className="py-12 text-center">
                <Star className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500 mb-4">No reviews yet. Be the first to add one!</p>
                <Link to={`/review/${clientId}`}>
                  <Button className="h-10 px-6 rounded-full bg-[#D97757] text-white hover:bg-[#C06040]">
                    Add First Review
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div 
                    key={review.id}
                    className="p-6 rounded-xl bg-stone-50 border border-stone-100"
                    data-testid={`review-${review.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                          {review.is_anonymous ? (
                            <User className="w-5 h-5 text-stone-500" />
                          ) : (
                            <span className="text-stone-600 font-medium">
                              {review.reviewer_name?.charAt(0) || 'A'}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">
                            {review.is_anonymous ? 'Anonymous' : review.reviewer_name}
                          </p>
                          <p className="text-xs text-stone-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(review.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Edit and Delete buttons - only show for user's own reviews */}
                        {review.reviewer_id === user?.id && (
                          <>
                            <button
                              onClick={() => openEditModal(review)}
                              className="p-2 rounded-full text-stone-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                              title="Edit your review"
                              data-testid={`edit-btn-${review.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteReview(review.id)}
                              disabled={deletingId === review.id}
                              className="p-2 rounded-full text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                              title="Delete your review"
                              data-testid={`delete-btn-${review.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {/* Report button - only show for other users' reviews */}
                        {review.reviewer_id !== user?.id && (
                          <button
                            onClick={() => handleReportReview(review.id)}
                            disabled={reportingId === review.id}
                            className="p-2 rounded-full text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                            title="Report this review"
                            data-testid={`report-btn-${review.id}`}
                          >
                            <Flag className="w-4 h-4" />
                          </button>
                        )}
                        <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-stone-200">
                          <Star className="w-4 h-4 fill-[#D97757] text-[#D97757]" />
                          <span className="font-semibold text-stone-900">{review.overall_rating.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <RatingBar label="Service Quality" value={review.service_quality_rating || review.hair_type_rating || 0} />
                      <RatingBar label="Punctuality" value={review.punctuality_rating} />
                      <RatingBar label="Payment" value={review.payment_rating} />
                      <RatingBar label="Communication" value={review.communication_rating} />
                    </div>

                    {(review.general_notes || review.service_notes || review.hair_notes) && (
                      <div className="pt-4 border-t border-stone-200 space-y-3">
                        {review.general_notes && (
                          <div>
                            <p className="text-stone-800">{review.general_notes}</p>
                          </div>
                        )}
                        {(review.service_notes || review.hair_notes) && (
                          <div className="mt-2 p-3 bg-stone-100 rounded-lg">
                            <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-1">Service Details</p>
                            <p className="text-sm text-stone-600">{review.service_notes || review.hair_notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Review Modal */}
      {editingReview && editFormData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-stone-900">Edit Review</h3>
              <button
                onClick={closeEditModal}
                className="p-2 rounded-full hover:bg-stone-100 transition-colors"
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              {/* Rating inputs */}
              {[
                { key: 'service_quality_rating', label: 'Service Quality' },
                { key: 'punctuality_rating', label: 'Punctuality' },
                { key: 'payment_rating', label: 'Payment' },
                { key: 'communication_rating', label: 'Communication' }
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-stone-700 mb-2">{label}</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setEditFormData({ ...editFormData, [key]: star })}
                        className="p-1 transition-colors"
                        data-testid={`edit-${key}-${star}`}
                      >
                        <Star
                          className={`w-7 h-7 ${
                            star <= editFormData[key]
                              ? 'fill-[#D97757] text-[#D97757]'
                              : 'fill-stone-200 text-stone-200'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Service Notes */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Service Notes
                </label>
                <textarea
                  value={editFormData.service_notes}
                  onChange={(e) => setEditFormData({ ...editFormData, service_notes: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-stone-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F8D4E] focus:border-transparent"
                  placeholder="Details about the service provided..."
                  data-testid="edit-service-notes"
                />
              </div>

              {/* General Notes */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  General Notes
                </label>
                <textarea
                  value={editFormData.general_notes}
                  onChange={(e) => setEditFormData({ ...editFormData, general_notes: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-stone-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F8D4E] focus:border-transparent"
                  placeholder="Any additional comments..."
                  data-testid="edit-general-notes"
                />
              </div>

              {/* Anonymous toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="edit-anonymous"
                  checked={editFormData.is_anonymous}
                  onChange={(e) => setEditFormData({ ...editFormData, is_anonymous: e.target.checked })}
                  className="w-4 h-4 rounded border-stone-300 text-[#5F8D4E] focus:ring-[#5F8D4E]"
                  data-testid="edit-anonymous-checkbox"
                />
                <label htmlFor="edit-anonymous" className="text-sm text-stone-700">
                  Post anonymously
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-stone-200 flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeEditModal}
                disabled={savingEdit}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="px-4 bg-[#5F8D4E] hover:bg-[#4a7340] text-white"
                data-testid="save-edit-btn"
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
